/**
 * Search payload utility functions
 *
 * Provides utilities for creating and transforming search payloads
 * for API requests. Handles payload creation, provider ID extraction,
 * filter value formatting, and result merging.
 *
 * Feature 060: Removed SERVICE_RANGE_OPTIONS and ServiceRangeOption imports
 */

import type { ApiProviderProfile } from '../types/matchmaking.types';

/**
 * Search filter parameters
 * Contains location filters for search
 *
 * Feature 060: distanceMiles and serviceRange removed from UI but kept in payload
 * for backward compatibility with stored procedures. These fields are populated with
 * values that match all providers (distanceMiles=10000, serviceRange=['IN_OFFICE','VIRTUAL','IN_HOME'])
 * while the stored procedure enforces strict geographic coverage based on county/state matching.
 */
export type SearchFilters = {
  state: string | null;
  county: string | null;
  address: string | null;
  zipCode: string | null;
  latitude: number | null;
  longitude: number | null;
  distanceMiles: number | null; // Feature 060: Set to 10000 (large radius for backward compat, geographic coverage takes precedence)
  serviceRange: string[] | null; // Feature 060: Set to all ranges ['IN_OFFICE','VIRTUAL','IN_HOME'] for backward compat, geographic coverage takes precedence
};

/**
 * Client context information
 * Contains relationship, reason, and age range data
 */
export type ClientContext = {
  relationship: string | null;
  reason: string | null;
  age_range: string | null;
};

/**
 * Answers from the matchmaking stepper questions
 * Contains three question answers (q1, q2, q3)
 */
export type StepperAnswers = {
  answer_q1: string;
  answer_q2: string;
  answer_q3: string;
};

/**
 * Complete search payload structure
 * Contains all data needed to create a matchmaking search
 *
 * FEATURE 025: Added userType to route Care Seeker searches correctly
 */
export type SearchPayload = {
  advisorId: string;
  clientId: string;
  promptText: string;
  userType?: 'CARE_SEEKER' | 'FINANCIAL_ADVISOR' | 'SERVICE_PROVIDER'; // FEATURE 025, 036: Routes searches for CS, FA, and SP
  filters: SearchFilters;
  clientContext: ClientContext;
  answers: StepperAnswers;
};

/**
 * Creates a search payload from individual parameters
 *
 * Combines all search parameters (location, context, answers)
 * into a structured payload for the backend API.
 *
 * Feature 060: Removed miles and selectedServiceRanges parameters
 * Now uses county-level coverage matching only
 *
 * FEATURE 025: Added userType parameter to support Care Seeker routing
 *
 * @param advisorId - ID of the advisor performing the search
 * @param clientId - ID of the client being searched for
 * @param promptText - Search query/prompt text
 * @param selectedState - Selected state filter
 * @param selectedCounty - Selected county filter
 * @param selectedAddress - Selected address
 * @param selectedZipCode - Selected ZIP code
 * @param selectedLatitude - Selected latitude coordinate
 * @param selectedLongitude - Selected longitude coordinate
 * @param clientProfile - Client profile with relationship, reason, age range
 * @param answers - Stepper question answers
 * @param userType - User type for routing (CARE_SEEKER, FINANCIAL_ADVISOR, or SERVICE_PROVIDER)
 * @returns Complete search payload object
 */
export const createSearchPayload = (
  advisorId: string,
  clientId: string,
  promptText: string,
  selectedState: string,
  selectedCounty: string,
  selectedAddress: string,
  selectedZipCode: string,
  selectedLatitude: number | null,
  selectedLongitude: number | null,
  clientProfile: { relationship?: string | null; reasonForJoining?: string | null; ageRange?: string | null } | undefined,
  answers: StepperAnswers,
  userType?: 'CARE_SEEKER' | 'FINANCIAL_ADVISOR' | 'SERVICE_PROVIDER',
): SearchPayload => {
  return {
    advisorId,
    clientId,
    promptText: promptText.trim(),
    userType, // FEATURE 025: Pass through for API routing
    filters: {
      state: selectedState || null,
      county: selectedCounty || null,
      address: selectedAddress || null,
      zipCode: selectedZipCode || null,
      latitude: selectedLatitude,
      longitude: selectedLongitude,
      distanceMiles: 10000, // Feature 060: Large radius to match all providers (effective no limit)
      serviceRange: ['IN_OFFICE', 'VIRTUAL', 'IN_HOME'], // Feature 060: All ranges for backward compat (matches all)
    },
    clientContext: {
      relationship: clientProfile?.relationship || null,
      reason: clientProfile?.reasonForJoining || null,
      age_range: clientProfile?.ageRange || null,
    },
    answers: {
      answer_q1: answers.answer_q1?.trim() || '',
      answer_q2: answers.answer_q2?.trim() || '',
      answer_q3: answers.answer_q3?.trim() || '',
    },
  };
};

/**
 * Extracts provider IDs from provider array
 * 
 * Handles both providerID and providerId field names for compatibility.
 * Filters out undefined/null values.
 * 
 * @param providers - Array of provider objects
 * @returns Array of provider ID strings
 */
export const extractProviderIds = (
  providers: Array<{ providerID?: string; providerId?: string }>,
): string[] => {
  return providers
    .map((provider) => provider.providerID || provider.providerId)
    .filter((id: string | undefined): id is string => Boolean(id));
};

/**
 * Creates a comma-separated filter values string from provider IDs
 * 
 * Used to pass provider IDs to the backend matchmaking proxy as filter values.
 * 
 * @param providerIds - Array of provider ID strings
 * @returns Comma-separated string or null if empty
 */
export const createFilterValuesString = (providerIds: string[]): string | null => {
  return providerIds.length > 0 ? providerIds.join(',') : null;
};

/**
 * Creates payload for the backend matchmaking proxy
 *
 * Formats the search query, filter values, and answers for the backend BFF
 * endpoint. The vector store id and top_k are injected server-side, so they are
 * intentionally NOT part of this payload.
 *
 * @param matchingPrompt - Search query text
 * @param filterValues - Comma-separated provider IDs (optional)
 * @param answers - Stepper question answers
 * @returns Matchmaking proxy payload object
 */
export const createMatchmakingProxyPayload = (
  matchingPrompt: string,
  filterValues: string | null,
  answers: StepperAnswers,
) => {
  return {
    input_query: matchingPrompt,
    filter_values: filterValues,
    answer_q1: answers.answer_q1?.trim() || '',
    answer_q2: answers.answer_q2?.trim() || '',
    answer_q3: answers.answer_q3?.trim() || '',
  };
};

/**
 * Creates payload for saving search results
 * 
 * Transforms match results into the format expected by the save API.
 * Includes provider IDs, scores, AI data (rationale, badges), and feedback.
 * 
 * @param searchId - ID of the search
 * @param advisorId - ID of the advisor
 * @param clientId - ID of the client
 * @param matchResults - Record of match results keyed by rank
 * @returns Save results payload object
 */
export const createSaveResultsPayload = (
  searchId: number,
  advisorId: string,
  clientId: string,
  matchResults: Record<string, { provider_id: string; reason?: string; badges?: string[] }>,
) => {
  const resultsToSave = Object.entries(matchResults).map(([_, matchResult]) => {
    return {
      providerId: matchResult.provider_id,
      score: null,
      aiData: {
        aiRationale: matchResult.reason || '',
        badges: matchResult.badges || [],
      },
      feedback: null,
    };
  });

  return {
    searchId,
    advisorId,
    clientId,
    results: resultsToSave,
  };
};

/**
 * Merges provider profiles with match results
 * 
 * Combines provider profile data with match result data (badges, rationale).
 * Creates default profile structure if provider not found in profiles map.
 * 
 * @param providerProfilesMap - Map of provider profiles by ID
 * @param matchResultsRecord - Record of match results with badges
 * @returns Merged provider profiles map
 */
export const mergeProviderProfiles = (
  providerProfilesMap: Record<string, ApiProviderProfile & { name?: string; serviceArea?: any }>,
  matchResultsRecord: Record<string, { provider_id: string; badges?: string[] }>,
): Record<string, ApiProviderProfile & { name?: string; serviceArea?: any; aiRationale?: string; distanceMiles?: number }> => {
  const merged: Record<string, ApiProviderProfile & { name?: string; serviceArea?: any; aiRationale?: string; distanceMiles?: number }> = { ...providerProfilesMap };

  Object.entries(matchResultsRecord).forEach(([_, matchResult]) => {
    const providerId = matchResult.provider_id;
    const badges = matchResult.badges !== undefined ? matchResult.badges : (merged[providerId]?.badges || []);
    
    if (merged[providerId]) {
      merged[providerId] = {
        ...merged[providerId],
        badges: badges,
      };
    } else {
      merged[providerId] = {
        provider_id: providerId,
        acceptsNewClients: true,
        categories: [],
        specialties: [],
        expertise_areas: [],
        latitude: 0,
        longitude: 0,
        rating: 0,
        reviewsTotalNumber: 0,
        years_experience: 0,
        typical_cost_range_min: 0,
        typical_cost_range_max: 0,
        cost_unit: '',
        pricing_model: '',
        registeredStates: [],
        service_ranges: [],
        licensedRegions: [],
        emergency_response_available: false,
        availability_schedule: '',
        doc_type: '',
        badges: badges,
      };
    }
  });

  return merged;
};

